import React, { useState } from 'react';
import WaveformVisualizer from '../../Common/WaveformVisualizer.jsx';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import useAudioEngine from '../../../audio/useAudioEngine.js';
import '../../../styles/theme.css';

export function GrandReveal({
  chains = [],
  currentChainIndex = 0,
  audioEngine = null,
  onNextChain,
  onPrevChain,
  onFinishReveal,
}) {
  const currentChain = chains[currentChainIndex] || {};
  const entries = currentChain.entries || [];
  const [playingClipId, setPlayingClipId] = useState(null);
  const [playProgress, setPlayProgress] = useState(0);

  const audio = useAudioEngine('host');

  const handlePlayClip = async (entry) => {
    const clipId = entry.clipId || entry.clip?.id || (typeof entry === 'string' ? entry : null);
    if (!clipId) return;

    setPlayingClipId(clipId);
    setPlayProgress(0);
    // The playhead follows the audio clock. It used to be hardcoded to 0.5 —
    // a cursor frozen mid-clip that looked like a real position readout.
    const opts = {
      onProgress: (d) => setPlayProgress(Math.min(1, Math.max(0, d?.progress ?? 0))),
      onEnded: () => setPlayProgress(1),
    };
    try {
      if (audioEngine && typeof audioEngine.play === 'function') {
        await audioEngine.play(clipId, opts);
      } else if (audioEngine && typeof audioEngine.playClip === 'function') {
        await audioEngine.playClip(clipId, opts);
      } else if (audio && audio.play) {
        await audio.play(clipId, opts);
      }
    } catch {
      // Audio playback catch
    } finally {
      setTimeout(() => setPlayingClipId(null), 3000);
    }
  };

  const firstEntry = entries[0];
  const lastEntry = entries[entries.length - 1];

  return (
    <div
      className="grand-reveal-container"
      data-testid="grand-reveal"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
        boxSizing: 'border-box',
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      {/* Header Banner */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--sd-ground, #08090B)',
          padding: '20px 28px',
          borderRadius: '20px',
          border: '1px solid var(--sd-line, #1A1F25)',
        }}
      >
        <div>
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-teal, #3ACFCF)',
            fontWeight: 600,
          }}>
            GRAND REVEAL
          </span>
          <h2
            data-testid="reveal-chain-title"
            style={{
              margin: '8px 0 0 0',
              fontSize: '2rem',
              fontWeight: 800,
              color: 'var(--sd-ink, #EDEFF2)',
            }}
          >
            Telephone Chain {currentChainIndex + 1} of {chains.length || 1}
          </h2>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          {onPrevChain && (
            <button
              data-testid="prev-chain-btn"
              disabled={currentChainIndex === 0}
              onClick={onPrevChain}
              style={{
                padding: '0 16px',
                minHeight: '44px',
                borderRadius: '10px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line-2, #1C2229)',
                color: currentChainIndex === 0 ? 'var(--sd-dim-2, #5A646D)' : 'var(--sd-ink-2, #C6CDD4)',
                cursor: currentChainIndex === 0 ? 'not-allowed' : 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                fontWeight: 600,
              }}
            >
              ◀ Previous
            </button>
          )}
          {currentChainIndex < chains.length - 1 ? (
            <button
              data-testid="next-chain-btn"
              onClick={onNextChain}
              style={{
                padding: '0 18px',
                minHeight: '44px',
                borderRadius: '10px',
                background: 'var(--sd-teal, #3ACFCF)',
                border: '1px solid var(--sd-teal, #3ACFCF)',
                color: 'var(--sd-ground, #08090B)',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                fontWeight: 700,
              }}
            >
              Next Chain ▶
            </button>
          ) : (
            <button
              data-testid="finish-reveal-btn"
              onClick={onFinishReveal}
              style={{
                padding: '0 20px',
                minHeight: '44px',
                borderRadius: '10px',
                background: 'var(--sd-amber, #E0A458)',
                border: '1px solid var(--sd-amber, #E0A458)',
                color: 'var(--sd-ground, #08090B)',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                fontWeight: 700,
              }}
            >
              Finish Reveal & Vote 🗳️
            </button>
          )}
        </div>
      </div>

      {/* Evolution Summary Box */}
      {firstEntry && lastEntry && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
            padding: '24px',
          }}
        >
          <div style={{ flex: 1 }}>
            <span style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '11px',
              color: 'var(--sd-dim, #6E7982)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 600
            }}>
              Original Phrase ({firstEntry.authorName})
            </span>
            <div
              data-testid="original-phrase-display"
              style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--sd-teal, #3ACFCF)', marginTop: '6px' }}
            >
              "{firstEntry.text || firstEntry.clipTitle || 'Original'}"
            </div>
          </div>

          <div style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '1.3rem',
            color: 'var(--sd-amber, #E0A458)',
            fontWeight: 800,
            letterSpacing: '0.1em'
          }}>
            ➡️ DRIFT ➡️
          </div>

          <div style={{ flex: 1, textAlign: 'right' }}>
            <span style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '11px',
              color: 'var(--sd-dim, #6E7982)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 600
            }}>
              Final Result ({lastEntry.authorName})
            </span>
            <div
              data-testid="final-phrase-display"
              style={{ fontSize: '1.3rem', fontWeight: 800, color: 'var(--sd-rose, #E4738A)', marginTop: '6px' }}
            >
              "{lastEntry.text || lastEntry.clipTitle || 'Final Sound'}"
            </div>
          </div>
        </div>
      )}

      {/* Chain Entries Timeline */}
      <div
        data-testid="chain-timeline"
        style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}
      >
        {entries.map((entry, idx) => (
          <React.Fragment key={idx}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '20px',
                background: 'var(--sd-panel-2, #0E1116)',
                border: '1px solid var(--sd-line-2, #1C2229)',
                borderRadius: '16px',
                padding: '20px 24px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flex: '1 1 0', minWidth: 0 }}>
                <div
                  style={{
                    width: '38px',
                    height: '38px',
                    borderRadius: '10px',
                    background: entry.type === 'text' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-rose, #E4738A)',
                    color: 'var(--sd-ground, #08090B)',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </div>

                <div style={{ flex: '1 1 0', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      fontSize: '10.5px',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: entry.type === 'text' ? 'var(--sd-teal, #3ACFCF)' : 'var(--sd-rose, #E4738A)',
                      flexShrink: 0,
                    }}>
                      {entry.type.toUpperCase()}
                    </span>
                    <span style={{ fontSize: '0.9rem', color: 'var(--sd-dim, #6E7982)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      by {entry.authorName}
                    </span>
                  </div>

                  <div
                    title={entry.type === 'audio' ? formatClipTitle(entry.clipTitle || entry.clipId || entry, 100) : entry.text}
                    style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', marginTop: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {entry.type === 'text' ? (
                      <span>"{entry.text}"</span>
                    ) : (
                      <span>🔊 Sound Clip: <strong>{formatClipTitle(entry.clipTitle || entry.clipId || entry)}</strong></span>
                    )}
                  </div>
                </div>
              </div>

              {entry.type === 'audio' && (
                <button
                  data-testid={`play-clip-btn-${idx}`}
                  onClick={() => handlePlayClip(entry)}
                  style={{
                    minHeight: '44px',
                    minWidth: '44px',
                    padding: '0 18px',
                    borderRadius: '10px',
                    background: 'var(--sd-panel, #0D1015)',
                    border: '1px solid var(--sd-line, #1A1F25)',
                    color: 'var(--sd-teal, #3ACFCF)',
                    fontWeight: 700,
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  {playingClipId === entry.clipId ? 'Playing... 🔊' : 'Play Sound 🔊'}
                </button>
              )}
            </div>

            {idx < entries.length - 1 && (
              <div style={{
                textAlign: 'center',
                color: 'var(--sd-dim, #6E7982)',
                fontSize: '1.2rem',
                margin: '-4px 0',
                fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              }}>
                ↓
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {playingClipId && (
        <WaveformVisualizer
          isPlaying={playProgress < 1}
          progress={playProgress}
          duration={3.0}
          playerName="Telephone Audio"
          clipTitle={playingClipId}
        />
      )}
    </div>
  );
}

export default GrandReveal;
